/** The create/edit form for a master record.
 *
 * UI_TASTE.md: an inline form panel that opens **above the list**, not a
 * separate page. Two-column grid, uppercase labels, required fields marked
 * with a red asterisk, validation errors collected at the top, primary Save
 * and secondary Cancel.
 *
 * Three behaviours here are not cosmetic:
 *
 *   - the record's `version` travels with the save, so a concurrent change
 *     is refused rather than silently overwritten (AUTH_DESIGN.md §9);
 *   - a caller without `view_values` gets no input for the commercial
 *     fields at all — and the server refuses them too, so the missing input
 *     is a courtesy rather than the control;
 *   - a 409 is reported in the words the user needs: someone else changed
 *     this, reload.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import type { FormField, MasterSpec } from '@/types'
import { singularOf } from '@/lib/label'
import { ItemFamilyPanel, type Tag } from './ItemFamilyPanel'
import { useRights } from '@/lib/rights'
import { SearchSelect } from '@/components/SearchSelect'

type Row = Record<string, unknown>

interface Props {
  spec: MasterSpec
  /** Absent means create; present means edit. */
  record?: Row
  valuesVisible: boolean
  onSaved: () => void
  onCancel: () => void
}

export function MasterForm(
  { spec, record, valuesVisible, onSaved, onCancel }: Props,
) {
  const editing = record !== undefined

  /* The panel is OPT-IN. A spec without `panel` reaches none of the code
     below, and MasterForm.test.tsx asserts that the other twelve screens
     render exactly as they did. types.ts, `panel`. */
  const hasPanel = spec.panel === 'families'
  // Tagging is its own right. Without it the families are shown, and the
  // save below does not attempt a write that would be refused after the
  // item itself had already saved. RIGHTS_MODEL.md 6.1.
  const canTag = useRights().can('masters.family.modify')
  const [tags, setTags] = useState<Tag[]>([])

  useEffect(() => {
    if (!hasPanel || !editing) { setTags([]); return }
    let live = true
    api.get<{ tags?: Tag[] }>(`/${spec.path}/${String(record?.[spec.idKey])}/families`)
      .then(r => { if (live) setTags(r.tags ?? []) })
      .catch(() => { if (live) setTags([]) })
    return () => { live = false }
  }, [hasPanel, editing, spec.path, record?.[spec.idKey]])
  // The key is rendered separately, above — so it must not appear again in
  // the field loop. Every spec that helpfully declared its own key was
  // showing "Code" twice, on every master, since the first one was built.
  const fields = useMemo(
    () => (spec.fields ?? []).filter(
      f => f.key !== spec.idKey && (!f.value || valuesVisible)),
    [spec.fields, spec.idKey, valuesVisible],
  )

  // ...but the spec's own declaration is the better one: it carries the real
  // label and the help text ("e.g. KG, NOS, MTR"). Use it where it exists,
  // and fall back to a plain "Code" where it does not.
  const keyField = useMemo(
    () => (spec.fields ?? []).find(f => f.key === spec.idKey)
       ?? { key: spec.idKey, label: 'Code', required: true },
    [spec.fields, spec.idKey],
  )

  // Options fetched from other masters. A code typed from memory is a code
  // typed wrongly, and the sub-group codes here are bare numbers - nobody
  // should be expected to recall that FRAME is 1.
  const [lookups, setLookups] = useState<Record<string, Option[]>>({})
  useEffect(() => {
    let live = true
    const wanted = [
      ...(spec.fields ?? []).filter(f => f.optionsFrom),
      ...(spec.idOptionsFrom
          ? [{ key: '__id__', optionsFrom: spec.idOptionsFrom }] : []),
    ] as { key: string; optionsFrom: NonNullable<FormField['optionsFrom']> }[]
    if (wanted.length === 0) return
    Promise.all(wanted.map(async f => {
      const src = f.optionsFrom
      try {
        const r = await api.get<{ rows: Record<string, unknown>[] }>(
          `/${src.path}/`, src.query)
        return [f.key, r.rows.map((row: Record<string, unknown>) => ({
          value: String(row[src.value] ?? ''),
          label: `${row[src.value]} — ${row[src.label]}`,
        }))] as const
      } catch (err) {
        // A lookup that will not load must not block the form - but it must
        // not fail SILENTLY either. An empty dropdown with no explanation
        // looks like "there is nothing to choose", when the real answer is
        // usually "you cannot see that list yet". Say which.
        const why = err instanceof ApiError ? err.message : 'could not be loaded'
        return [f.key, [{ value: '', label: `— ${why} —` }]] as const
      }
    })).then(pairs => { if (live) setLookups(Object.fromEntries(pairs)) })
    return () => { live = false }
  }, [spec.fields, spec.idOptionsFrom])

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const f of spec.fields ?? []) {
      const v = record?.[f.key]
      initial[f.key] = v === null || v === undefined ? '' : String(v)
    }
    if (!editing) initial[spec.idKey] = String(record?.[spec.idKey] ?? '')
    return initial
  })

  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const firstInput = useRef<HTMLInputElement>(null)

  useEffect(() => { firstInput.current?.focus() }, [])

  const set = (key: string, v: string) =>
    setValues(prev => ({ ...prev, [key]: v }))

  function validate(): string[] {
    const found: string[] = []
    for (const f of fields) {
      if (f.required && !values[f.key]?.trim()) {
        found.push(`${f.label} is required.`)
      }
      if (f.type === 'number' && values[f.key] &&
          !Number.isFinite(Number(values[f.key]))) {
        found.push(`${f.label} must be a number.`)
      }
    }
    return found
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    const found = validate()
    setErrors(found)
    if (found.length) return

    // Only fields the form actually offered. A masked user's payload cannot
    // carry a rate, because there was no input to carry it.
    const payload: Record<string, unknown> = {}
    for (const f of fields) {
      if (editing && f.immutable) continue
      payload[f.key] = values[f.key] === '' ? null : values[f.key]
    }

    setSaving(true)
    let savedId = editing ? String(record?.[spec.idKey] ?? '') : ''
    try {
      if (editing) {
        payload.version = record?.version
        await api.update(`/${spec.path}/${String(record?.[spec.idKey])}`, payload)
      } else {
        payload[spec.idKey] = values[spec.idKey]
        const created = await api.create<Row>(`/${spec.path}`, payload)
        savedId = String(created?.[spec.idKey] ?? '')
      }

      /* Tags are written AFTER the record, because they point at it — and on
         a create the item's code does not exist until the server issues it.
         A failure here is reported rather than swallowed: the item is saved
         and its tags are not, and the user needs to know which. */
      if (hasPanel && savedId && canTag) {
        await api.update(`/${spec.path}/${savedId}/families`, { tags })
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError && err.conflict) {
        setErrors([err.message])       // already phrased for the user
      } else {
        setErrors([err instanceof Error ? err.message
                                        : 'The record could not be saved.'])
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="card form-card" onSubmit={save}>
      <div className="card-header">
        {editing ? `Edit ${singularOf(spec)}`
                 : `New ${singularOf(spec)}`}
      </div>

      {errors.length > 0 && (
        <div className="form-errors" role="alert">
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      <div className="form-grid">
        {!editing && !spec.idGenerated && spec.idOptionsFrom && (
          <Field
            field={{ ...keyField, required: true,
                     optionsFrom: spec.idOptionsFrom }}
            options={lookups['__id__']}
            value={values[spec.idKey] ?? ''}
            onChange={v => set(spec.idKey, v)}
          />
        )}
        {!editing && !spec.idGenerated && !spec.idOptionsFrom && (
          <Field
            field={{ ...keyField, required: true }}
            value={values[spec.idKey] ?? ''}
            onChange={v => set(spec.idKey, v)}
            inputRef={firstInput}
          />
        )}
        {!editing && spec.idGenerated && (
          // Say what will happen rather than showing a box the server
          // ignores. The code is derived from the classification chosen
          // below, which is what makes it meaningful.
          <div className="form-field">
            <label>{keyField.label}</label>
            <div className="form-generated">
              {spec.idGeneratedHelp ?? 'Generated when you save'}
            </div>
          </div>
        )}
        {fields.map(f => (
          <Field
            key={f.key}
            field={f}
            options={lookups[f.key]}
            value={values[f.key] ?? ''}
            onChange={v => set(f.key, v)}
            disabled={editing && f.immutable}
          />
        ))}
      </div>

      {hasPanel && (
        <ItemFamilyPanel
          itemCategory={String(values.item_category ?? 'Item')}
          tags={tags}
          onChange={setTags}
          readOnly={!canTag}
        />
      )}

      {/* A masked user is told the fields exist rather than left wondering
          why the form looks short. */}
      {!valuesVisible && (spec.fields ?? []).some(f => f.value) && (
        <p className="form-note">
          Commercial values are hidden for your account, so they cannot be
          edited here.
        </p>
      )}

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn-secondary"
                onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  )
}

interface Option { value: string; label: string }

function Field(
  { field, value, onChange, disabled, inputRef, options }: {
    field: FormField
    value: string
    onChange: (v: string) => void
    disabled?: boolean
    inputRef?: React.RefObject<HTMLInputElement>
    options?: Option[]
  },
) {
  const id = `f-${field.key}`
  return (
    <div className={field.type === 'textarea' ? 'form-field span-2' : 'form-field'}>
      <label htmlFor={id}>
        {field.label}
        {field.required && <span className="req" aria-hidden="true"> *</span>}
      </label>

      {field.type === 'textarea' ? (
        <textarea id={id} value={value} rows={3} disabled={disabled}
                  onChange={e => onChange(e.target.value)} />
      ) : field.type === 'select' || field.optionsFrom ? (
        /* Every dropdown in the ERP is searchable once the list is long
           enough to need it — UI_TASTE.md, "A Dropdown Is Searched, Not
           Scrolled". The item picker holds thousands of parts, and a native
           select only type-aheads from the START of a label. */
        <SearchSelect
          id={id}
          value={value}
          disabled={disabled}
          options={options ?? field.options ?? []}
          onChange={onChange}
        />
      ) : (
        <input
          id={id}
          ref={inputRef}
          type={field.type === 'number' ? 'number'
              : field.type === 'date' ? 'date'
              : field.type === 'email' ? 'email' : 'text'}
          value={value}
          disabled={disabled}
          placeholder={field.placeholder}
          onChange={e => onChange(e.target.value)}
        />
      )}

      {field.help && <div className="form-help">{field.help}</div>}
    </div>
  )
}
